import React from 'react';
import { ChatMessage as ChatMessageType } from '../types';
import ConfirmationCard from './ConfirmationCard';
import QuestionPrompt from './QuestionPrompt';
import UserAvatar, { User } from 'common/components/UserAvatar/UserAvatar';
import BEM from 'common/bem';

const classes = BEM.with('ChatMessage');

function renderMarkdown(text: string): React.ReactNode[] {
  // Split into lines first to handle block-level markdown
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];

  for (let li = 0; li < lines.length; li++) {
    let line = lines[li];

    // Skip horizontal rules
    if (/^-{3,}$/.test(line.trim())) continue;

    // Strip heading markers (## Heading → Heading)
    const headingMatch = line.match(/^(#{1,6})\s+(.*)/);
    if (headingMatch) {
      line = headingMatch[2];
    }

    // Strip leading "- " for list items
    const listMatch = line.match(/^-\s+(.*)/);
    if (listMatch) {
      line = listMatch[1];
    }

    if (!line.trim()) continue;

    // Inline markdown: **bold** and *italic*
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIndex) {
        parts.push(line.slice(lastIndex, match.index));
      }
      if (match[2]) {
        parts.push(<strong key={`${li}-${match.index}`}>{match[2]}</strong>);
      } else if (match[3]) {
        parts.push(<em key={`${li}-${match.index}`}>{match[3]}</em>);
      }
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
      parts.push(line.slice(lastIndex));
    }

    if (result.length > 0) {
      result.push(<br key={`br-${li}`} />);
    }

    if (headingMatch) {
      result.push(<strong key={`h-${li}`}>{parts.length > 0 ? parts : [line]}</strong>);
    } else {
      result.push(...(parts.length > 0 ? parts : [line]));
    }
  }

  return result;
}

type ChatMessageProps = {
  message: ChatMessageType;
  currentUser?: User;
  onSelectOption: (value: string, label?: string) => void;
  isLastAssistantMessage: boolean;
  hasUserReplied: boolean;
};

export default function ChatMessage({
  message,
  currentUser,
  onSelectOption,
  isLastAssistantMessage,
  hasUserReplied,
}: ChatMessageProps) {
  const isUser = message.role === 'user';

  if (isUser) {
    return (
      <div className={classes()}>
        <div className={classes('user-row')}>
          <div className={classes('user-bubble')}>{message.content}</div>
          <UserAvatar className={classes('user-avatar')} user={currentUser} size={32} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes()}>
      <div className={classes('assistant-group')}>
        <div className={classes('assistant-header')}>
          <div className={classes('avatar')}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <span className={classes('sender')}>TigerConnect Assistant</span>
        </div>

        {message.segments.map((segment, i) => {
          if (segment.kind === 'text') {
            const rendered = renderMarkdown(segment.content);
            if (rendered.length === 0) return null;
            return (
              <div key={i} className={classes('assistant-bubble')}>
                {rendered}
              </div>
            );
          }
          if (segment.kind === 'confirmation') {
            return <ConfirmationCard key={i} data={segment.data} />;
          }
          if (segment.kind === 'question') {
            const answered = !isLastAssistantMessage || hasUserReplied;
            if (answered) return null;
            return (
              <QuestionPrompt
                key={i}
                data={segment.data}
                onSelect={onSelectOption}
                disabled={!!message.isStreaming}
              />
            );
          }
          return null;
        })}

        {message.isStreaming && message.segments.length === 0 && (
          <div className={classes('assistant-bubble')}>
            <span className={classes('cursor')} />
          </div>
        )}

        {message.error && <div className={classes('error')}>{message.error}</div>}
      </div>
    </div>
  );
}
